import React from "react";
import { createPortal } from "react-dom";
import type { ComputePosFn } from "../../../shared/chat/positioning";
import { useTranslation } from "react-i18next";

export interface Message {
  _id: string;
  sender: any;
  recipient: any;
  albumIds?: string[];
  streamUid?: string;
  isExpiredMedia?: boolean;
  expiresAt?: string;
  receiverId?: string;

  text: string;
  createdAt: string;

  mediaKeyEnvelope?: { nonce: string; ciphertext: string; ephPub: string };
  mediaKeySelfEnvelope?: { nonce: string; ciphertext: string; ephPub: string };

  decryptedText?: string | null;
  decryptedMediaUrl?: string | null;

  media?: string;
  mediaMime?: string;
  mediaKey?: string;

  replyToId?: string;

  isEncrypted?: boolean;

  ciphertext?: string;
  ciphertextSelf?: string;
  cipherAlgo?: string;
  senderPub?: string;
  recipientPub?: string;

  mediaIv?: string;
  mediaAlgo?: string;

  wrappedKeys?: Record<string, string>;

  signal?: {
    msgType: number;
    bodyB64: string;
    version?: number;
  };

  signals?: Record<
    string,
    {
      msgType?: number;
      type?: number;
      bodyB64: string;
      version?: number;
    }
  >;
}

interface Props {
  msg: Message;
  mine: boolean;
  hover: boolean;
  onHoverChange: (hovering: boolean) => void;
  onCopy: (text: string) => void;
  onReply: (msg: Message) => void;
  onDelete: (id: string, mine: boolean) => void;
  findMessageById: (id?: string) => Message | undefined;
  resolveUserById?: (id: string) => any;
  ui: any;
  msgMenuOpenId: string | null;
  msgMenuStyle: React.CSSProperties | null;
  setMsgMenuOpenId: React.Dispatch<React.SetStateAction<string | null>>;
  setMsgMenuStyle: React.Dispatch<React.SetStateAction<React.CSSProperties | null>>;
  computeAutoPosition: ComputePosFn;
  renderMedia?: (
    m: Message,
    ctx: { mine: boolean; computeAutoPosition: ComputePosFn }
  ) => React.ReactNode;
  showMedia?: (idOrIds: string | string[]) => void;
  showMediaFor?: (msg: Message) => void;
  replyLookupTick: number;
}

function isAlbumManifestText(t: string) {
  const s = (t || "").trim();
  if (!s || s[0] !== "{") return false;
  try {
    const j = JSON.parse(s);
    const hasItems = Array.isArray(j?.items);
    const hasIds = Array.isArray(j?.ids);
    return j?.kind === "album" && (hasItems || hasIds);
  } catch {
    return false;
  }
}

function formatChatDayLabel(
  d: Date,
  t?: (key: string, opts?: any) => string
): string {
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfToday.getDate() - 1);

  if (d >= startOfToday) {
    return t?.("chat.date.today") || "Today";
  }

  if (d >= startOfYesterday && d < startOfToday) {
    return t?.("chat.date.yesterday") || "Yesterday";
  }

  // older: show date
  return d.toLocaleDateString();
}


// Media Rendering
function isCloudflareStream(m: Message) {
  const url = String(m.media || "");
  const mm = String(m.mediaMime || "").toLowerCase();
  return (
    mm === "video/cloudflare-stream" ||
    /iframe\.videodelivery\.net/i.test(url) ||
    !!m.streamUid
  );
}

function streamSrc(m: Message) {
  return m.streamUid
    ? `https://iframe.videodelivery.net/${m.streamUid}`
    : String(m.media || "");
}

type MediaLabels = {
  expired: string;
  mediaExpiredTitle: string;
  showMedia: string;
  showMediaWithCount: (count: number) => string;
};

function renderMediaDefault(
  m: Message,
  mine: boolean,
  computeAutoPosition: ComputePosFn,
  showMedia: ((idOrIds: string | string[]) => void) | undefined,
  showMediaFor: ((m: Message) => void) | undefined,
  labels: MediaLabels
): React.ReactNode {
  const albumItemsRaw = (m as any).albumItems;
  const albumItems =
    Array.isArray(albumItemsRaw) && albumItemsRaw.length ? albumItemsRaw : null;

  const albumIds =
    Array.isArray(m.albumIds) && m.albumIds.length
      ? m.albumIds
      : (() => {
          const raw = (m as any).decryptedText;
          const s = (typeof raw === "string" ? raw : (m.text ?? "")).trim();
          if (!s || s[0] !== "{") return null;
          try {
            const j = JSON.parse(s);
            if (j?.kind !== "album") return null;

            const fromItems = Array.isArray(j.items)
              ? j.items
                  .map((it: any) => String(it?.id))
                  .filter(Boolean)
              : [];

            const fromIds = Array.isArray(j.ids)
              ? j.ids.map((x: any) => String(x)).filter(Boolean)
              : [];

            const all = [...fromItems, ...fromIds];
            return all.length ? all : null;
          } catch {}
          return null;
        })();

  const albumCount = albumItems?.length || (albumIds ? albumIds.length : 0);

  const mm = String(m.mediaMime || "").toLowerCase();
  const expired = Boolean(
    (m as any).isExpiredMedia ||
      (m.expiresAt && Date.now() >= Date.parse(m.expiresAt))
  );

  const ExpiredBadge = (
    <div
      style={{
        width: 320,
        maxWidth: "80vw",
        borderRadius: 8,
        marginBottom: 6,
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        color: "#64748b",
        fontWeight: 600,
        padding: "10px 12px",
        textAlign: "center",
      }}
    >
      {labels.expired}
    </div>
  );

  // Album bubble
  if (albumCount > 0) {
    const onClick = () => {
      if (expired) return;
      if (showMediaFor) return showMediaFor(m);
      if (albumIds && albumIds.length) {
        return showMedia?.(albumIds);
      }
    };

    return (
      <div style={{ marginBottom: 6 }}>
        <button
          type="button"
          onClick={onClick}
          disabled={expired}
          aria-disabled={expired}
          title={expired ? labels.mediaExpiredTitle : labels.showMedia}
          style={{
            padding: "8px 12px",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
            background: "#fff",
            cursor: expired ? "not-allowed" : "pointer",
            fontWeight: 600,
            opacity: expired ? 0.6 : 1,
          }}
        >
          {expired
            ? labels.expired
            : labels.showMediaWithCount(albumCount)}
        </button>
      </div>
    );
  }

  // Cloudflare stream
  if (isCloudflareStream(m)) {
    if (expired) return ExpiredBadge;
    const src = streamSrc(m);
    return (
      <div
        style={{
          position: "relative",
          width: 320,
          maxWidth: "80vw",
          aspectRatio: "16 / 9",
          borderRadius: 8,
          overflow: "hidden",
          background: "#000",
          marginBottom: 6,
        }}
      >
        <iframe
          src={src}
          title="Video"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            border: 0,
          }}
        />
      </div>
    );
  }

  // Clear image
  if (m.media && mm.startsWith("image/")) {
    if (expired) return ExpiredBadge;
    return (
      <img
        src={m.media}
        alt=""
        style={{
          width: 320,
          maxWidth: "80vw",
          borderRadius: 8,
          display: "block",
          marginBottom: 6,
        }}
      />
    );
  }

  // Clear video
  if (m.media && mm.startsWith("video/")) {
    if (expired) return ExpiredBadge;
    return (
      <video
        src={m.media}
        controls
        playsInline
        preload="metadata"
        style={{
          width: 320,
          maxWidth: "80vw",
          borderRadius: 8,
          display: "block",
          marginBottom: 6,
          background: "#000",
        }}
      />
    );
  }

  // Encrypted media
  const hasEncryptedMedia = !!m.mediaIv || !!m.mediaKeyEnvelope;
  if (hasEncryptedMedia) {
    const onClick = () => {
      if (expired) return;
      return showMediaFor ? showMediaFor(m) : showMedia?.(String(m._id));
    };
    return (
      <div style={{ marginBottom: 6 }}>
        <button
          type="button"
          onClick={onClick}
          disabled={expired}
          aria-disabled={expired}
          title={expired ? labels.mediaExpiredTitle : labels.showMedia}
          style={{
            padding: "8px 12px",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
            background: "#fff",
            cursor: expired ? "not-allowed" : "pointer",
            fontWeight: 600,
            opacity: expired ? 0.6 : 1,
          }}
        >
          {expired ? labels.expired : labels.showMedia}
        </button>
      </div>
    );
  }

  return null;
}

function getProfileName(u: any) {
  return (
    u?.profileName ||      // <-- most likely the real field
    u?.profilename ||      // <-- what your code currently expects
    u?.displayName ||
    u?.name ||
    u?.username ||
    null
  );
}

/* ---------- Component ---------- */
const MessageOutlookBase: React.FC<Props> = ({
  msg,
  mine,
  hover,
  onHoverChange,
  onCopy,
  onReply,
  onDelete,
  findMessageById,
  renderMedia,
  ui,
  msgMenuOpenId,
  msgMenuStyle,
  setMsgMenuOpenId,
  setMsgMenuStyle,
  computeAutoPosition,
  showMedia,
  showMediaFor,
  replyLookupTick,
}) => {
  const { t } = useTranslation();

  const created = msg.createdAt ? new Date(msg.createdAt) : new Date();
  const dateString = formatChatDayLabel(created, t);
  const timeString = created.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const mediaLabels: MediaLabels = {
    expired: t("chat.message.media.expired"),
    mediaExpiredTitle: t("chat.message.media.expiredTitle"),
    showMedia: t("chat.message.media.show"),
    showMediaWithCount: (count: number) =>
      t("chat.message.media.showWithCount", { count }),
  };

  
  const mediaNode =
    typeof renderMedia === "function"
      ? renderMedia(msg, { mine, computeAutoPosition }) ??
        renderMediaDefault(
          msg,
          mine,
          computeAutoPosition,
          showMedia,
          showMediaFor,
          mediaLabels
        )
      : renderMediaDefault(
          msg,
          mine,
          computeAutoPosition,
          showMedia,
          showMediaFor,
          mediaLabels
        );

  return (
    <div data-message style={ui.row(mine)}>
      <div style={ui.aligner(mine)}>
        <div
          style={ui.bubble(mine)}
          onMouseEnter={() => onHoverChange(true)}
          onMouseLeave={() => onHoverChange(false)}
        >
          {/* Quoted original */}
{/* Quoted original */}
{msg.replyToId &&
  (() => {
    // Prefer stable preview fields on the message (prevents flicker)
    const previewAuthor = (msg as any).replyPreviewAuthor as string | undefined;
    const previewText   = (msg as any).replyPreviewText as string | undefined;

    // Fallback: try to find the original message
    const orig = findMessageById(msg.replyToId);

    // If we have neither preview nor original, render nothing
    if (!previewAuthor && !previewText && !orig) return null;

    const senderObj = (orig as any)?.sender;

    const author =
      previewAuthor ||
      getProfileName(senderObj) ||
      t("chat.message.quoted.unknownUser");

    // derive text from preview first, else from orig
    let text = (previewText ?? "").trim();
    if (!text && orig) {
      const qRaw = (orig as any).decryptedText;
      const qSrc = (typeof qRaw === "string" ? qRaw : orig.text ?? "").trim();
      text = isAlbumManifestText(qSrc) ? "" : qSrc;
    }

    return (
      <div style={ui.quotedBox}>
        <div style={ui.quotedLine}>
          <span style={ui.quotedAuthor}>{author}:</span>
          <span style={ui.quotedText}>{text}</span>
        </div>
      </div>
    );
  })()}



          {/* media preview */}
          {mediaNode}

          {/* text */}
          {(() => {
            const raw = (msg as any).decryptedText;
            const s = (typeof raw === "string"
              ? raw
              : msg.text ?? ""
            ).trim();

            if (isAlbumManifestText(s)) return null;
            return s ? (
              <div style={ui.bubbleContent}>{s}</div>
            ) : null;
          })()}

          {/* meta row: ⋯ + time */}
          <div style={ui.metaRow}>
            <div style={ui.kebabSlot}>
              <button
                type="button"
                data-role="msg-kebab"
                title={t("chat.message.menu.optionsTitle")}
                aria-haspopup="menu"
                aria-expanded={msgMenuOpenId === msg._id}
                style={ui.dotBtnMsg(mine, hover)}
                onClick={(e) => {
                  e.stopPropagation();
                  const anchorEl = e.currentTarget as HTMLElement;
                  const style = computeAutoPosition(anchorEl, 180, 120, {
                    gap: -8,
                    preferSide: "top",
                    preferAlign: mine ? "end" : "start",
                  });
                  setMsgMenuStyle(style);
                  setMsgMenuOpenId((prev) =>
                    prev === msg._id ? null : msg._id
                  );
                }}
              >
                ⋯
              </button>
            </div>

            <div style={ui.timeInside(mine)}>
              {dateString} • {timeString}
            </div>
          </div>

          {/* floating message menu (portal) */}
          {msgMenuOpenId === msg._id &&
            msgMenuStyle &&
            createPortal(
              <div
                data-role="msg-menu"
                style={{ ...ui.msgMenuBase, ...msgMenuStyle }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  style={ui.itemMenuItem}
                  onClick={() => onCopy(msg.decryptedText ?? msg.text)}
                >
                  {t("chat.message.menu.copy")}
                </button>
                <button
                  type="button"
                  style={ui.itemMenuItem}
                  onClick={() => onReply(msg)}
                >
                  {t("chat.message.menu.reply")}
                </button>
                <button
                  type="button"
                  style={ui.itemMenuItemDanger}
                  onClick={() => onDelete(msg._id, mine)}
                >
                  {t("chat.message.menu.delete")}
                </button>
              </div>,
              document.body
            )}
        </div>
      </div>
    </div>
  );
};

/** Memoize to avoid re-render storms while typing/receiving */
const MessageOutlook = React.memo(
  MessageOutlookBase,
  (prev, next) =>
    prev.msg === next.msg &&
    prev.mine === next.mine &&
    prev.hover === next.hover &&
    prev.msgMenuOpenId === next.msgMenuOpenId &&
    prev.msgMenuStyle === next.msgMenuStyle &&
    prev.ui === next.ui &&
    prev.replyLookupTick === next.replyLookupTick
);

const ui = {
  row: (mine: boolean) =>
    ({
      display: "grid",
      gridTemplateColumns: "1fr",
      width: "100%",
      paddingLeft: 4,
      paddingRight: 4,
      alignItems: "end",
      gap: 8,
    }) as React.CSSProperties,

  aligner: (mine: boolean) =>
    ({
      display: "flex",
      width: "100%",
      minWidth: 0,
      justifyContent: mine ? "flex-end" : "flex-start",
    }) as React.CSSProperties,

  bubble: (mine: boolean) =>
    ({
      position: "relative",
      display: "grid",
      gridTemplateRows: "auto auto",
      rowGap: 3,
      marginBottom: 4,
      width: "fit-content",
      minWidth: 250,
      maxWidth: 250,
      justifySelf: mine ? "end" : "start",
      padding: "5px 8px",
      borderRadius: 14,
      borderTopLeftRadius: mine ? 14 : 6,
      borderTopRightRadius: mine ? 6 : 14,
      background: mine ? "#5b21b6" : "#f8fafc",
      color: mine ? "#ffffff" : "#0f172a",
      border: mine ? "1px solid #4c1d95" : "1px solid #e2e8f0",
      boxShadow: "0 1px 2px rgba(15,23,42,.05)",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      textAlign: "left",
      cursor: "text",
      transition: "none",
      transform: "none",
    }) as React.CSSProperties,

  bubbleContent: {
    cursor: "text",
  } as React.CSSProperties,

  metaRow: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    color: "#94a3b8",
  } as React.CSSProperties,

  kebabSlot: {
    display: "inline-block",
  } as React.CSSProperties,

  dotBtnMsg: (mine: boolean, visible: boolean) =>
    ({
      background: "transparent",
      border: "none",
      cursor: "pointer",
      fontSize: 18,
      lineHeight: 1,
      padding: 0,
      margin: 0,
      color: mine ? "#fff" : "inherit",
      opacity: visible ? 1 : 0,
      visibility: visible ? "visible" : "hidden",
      pointerEvents: visible ? "auto" : "none",
      transition: "opacity 120ms ease",
    }) as React.CSSProperties,

  timeInside: (mine: boolean) =>
    ({
      justifySelf: "end",
      fontSize: 11,
      opacity: mine ? 0.9 : 0.7,
      color: mine ? "rgba(255,255,255,.9)" : "#64748b",
      userSelect: "none",
      lineHeight: 1,
    }) as React.CSSProperties,

  msgMenuBase: {
    background: "#1b1b1b",
    color: "#ddd",
    border: "1px solid #2a2a2a",
    borderRadius: 10,
    padding: 6,
    minWidth: 160,
    zIndex: 10000,
    boxShadow: "0 8px 18px rgba(0,0,0,.35)",
  } as React.CSSProperties,

  itemMenuItem: {
    display: "block",
    width: "100%",
    textAlign: "left" as const,
    padding: "8px 10px",
    background: "transparent",
    border: "none",
    color: "#ddd",
    cursor: "pointer",
    borderRadius: 8,
  } as React.CSSProperties,

  itemMenuItemDanger: {
    display: "block",
    width: "100%",
    textAlign: "left" as const,
    padding: "8px 10px",
    background: "transparent",
    border: "none",
    color: "#ff6b6b",
    cursor: "pointer",
    borderRadius: 8,
  } as React.CSSProperties,

  quotedBox: {
    borderLeft: "3px solid rgba(0,0,0,.25)",
    background: "rgba(255,255,255,.35)",
    borderRadius: 8,
    padding: "4px 6px",
    marginBottom: 6,
    maxWidth: "100%",
    overflow: "hidden",
    cursor: "text",
  } as React.CSSProperties,

  quotedLine: {
    display: "flex",
    alignItems: "baseline",
    gap: 4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  } as React.CSSProperties,

  quotedAuthor: {
    fontSize: 12,
    fontWeight: 600,
    color: "#12161c",
    opacity: 0.9,
  } as React.CSSProperties,

  quotedText: {
    fontSize: 12,
    color: "#12161c",
  } as React.CSSProperties,
};

export { ui };
export default MessageOutlook;
